export const DOCUMENT_FILTERS = [
  { id: 'ALL', label: 'Tous' },
  { id: 'QUOTE', label: 'Devis' },
  { id: 'CONTRACT', label: 'Contrats' },
  { id: 'INVOICE', label: 'Factures' },
  { id: 'CREDIT_NOTE', label: 'Avoirs' },
];

export const EMPTY_DOCUMENTS_MESSAGE = 'Aucun document disponible pour le moment.';

export const COMPANY_FILTER_ALL = 'ALL';

export function filterDocuments(documents, filterId) {
  const list = Array.isArray(documents) ? documents : [];
  if (!filterId || filterId === 'ALL') return list;
  return list.filter((doc) => doc.type === filterId);
}

/** Sociétés NR2J présentes dans la liste sécurisée (sans doublon). */
export function listLinkedOrganizations(documents) {
  const byId = new Map();
  for (const doc of documents || []) {
    const org = doc?.organization;
    if (!org?.id || byId.has(org.id)) continue;
    byId.set(org.id, {
      id: org.id,
      displayName: org.displayName || 'Société',
    });
  }
  return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'));
}

export function filterDocumentsByCompany(documents, companyId) {
  const list = Array.isArray(documents) ? documents : [];
  if (!companyId || companyId === COMPANY_FILTER_ALL) return list;
  return list.filter((doc) => doc.organization?.id === companyId);
}

export function shouldShowCompanyFilter(documents) {
  return listLinkedOrganizations(documents).length > 1;
}

export function documentOrganizationLabel(doc) {
  return doc?.organization?.displayName || '—';
}

export function buildQuoteAcceptConfirmationMessage(doc) {
  const company = documentOrganizationLabel(doc);
  const amount = formatDocumentAmount(doc?.amountTtcCents, doc?.currency);
  return `Vous allez accepter le devis ${doc?.number || ''} pour la société ${company} d’un montant de ${amount} TTC.`;
}

export function buildQuoteRefuseConfirmationMessage(doc) {
  const company = documentOrganizationLabel(doc);
  return `Vous allez refuser le devis ${doc?.number || ''} pour la société ${company}.`;
}

export const MERCHANT_DOCUMENTS_UPDATED = 'merchant-documents-updated';

/** Nombre de documents dont `actionRequired` est déjà vrai. Pas de second état métier. */
export function countDocumentActionsRequired(documents) {
  return (documents || []).filter((doc) => doc?.actionRequired === true).length;
}

export function publishMerchantDocuments(documents) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MERCHANT_DOCUMENTS_UPDATED, {
    detail: { documents: documents || [] },
  }));
}

export function sortDocuments(documents) {
  return [...(documents || [])].sort((a, b) => {
    if (Boolean(a.actionRequired) !== Boolean(b.actionRequired)) {
      return a.actionRequired ? -1 : 1;
    }
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });
}

export function formatDocumentAmount(cents, currency = 'EUR') {
  if (cents === null || cents === undefined || cents === '') return '—';
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(value);
}

export function formatDocumentDate(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeZone: 'Europe/Paris',
  }).format(date);
}

/** Classes alignées sur `admin-web/src/utils/nr2jConstants.js` (badges NR2J). */
export const MERCHANT_DOCUMENT_STATUS_BADGE = Object.freeze({
  success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-50 text-amber-900 border-amber-200',
  vigilance: 'bg-orange-50 text-orange-900 border-orange-200',
  danger: 'bg-rose-50 text-rose-800 border-rose-200',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
});

const STATUS_LABEL_SUCCESS = new Set(['Accepté', 'Signé', 'Payée', 'Émis', 'Émise']);
const STATUS_LABEL_WARNING = new Set(['À valider', 'Partiellement payée']);
const STATUS_LABEL_VIGILANCE = new Set(['Impayée']);
const STATUS_LABEL_DANGER = new Set(['Refusé', 'Annulé', 'Expiré', 'En retard']);

function badgeClassForDocumentStatus(doc) {
  const label = String(doc?.statusLabel || '').trim();
  if (STATUS_LABEL_SUCCESS.has(label)) return MERCHANT_DOCUMENT_STATUS_BADGE.success;
  if (STATUS_LABEL_WARNING.has(label)) return MERCHANT_DOCUMENT_STATUS_BADGE.warning;
  if (STATUS_LABEL_VIGILANCE.has(label)) return MERCHANT_DOCUMENT_STATUS_BADGE.vigilance;
  if (STATUS_LABEL_DANGER.has(label)) return MERCHANT_DOCUMENT_STATUS_BADGE.danger;

  const status = doc?.status;
  if (status === 'ACCEPTED' || status === 'SIGNED' || status === 'PAID' || status === 'ISSUED') {
    return MERCHANT_DOCUMENT_STATUS_BADGE.success;
  }
  if (status === 'PARTIAL') return MERCHANT_DOCUMENT_STATUS_BADGE.warning;
  if (status === 'UNPAID') return MERCHANT_DOCUMENT_STATUS_BADGE.vigilance;
  if (status === 'REFUSED' || status === 'CANCELLED' || status === 'EXPIRED') {
    return MERCHANT_DOCUMENT_STATUS_BADGE.danger;
  }
  if (doc?.actionRequired && (status === 'SENT' || label === 'À valider')) {
    return MERCHANT_DOCUMENT_STATUS_BADGE.warning;
  }
  return MERCHANT_DOCUMENT_STATUS_BADGE.neutral;
}

/** Badge statut centre Documents (libellé serveur inchangé). */
export function merchantDocumentStatusBadge(doc) {
  const label = doc?.statusLabel || '—';
  return {
    label,
    className: badgeClassForDocumentStatus(doc),
  };
}
